/* eslint-disable react-hooks/rules-of-hooks */
import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Col, Row } from 'antd';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { ReactComponent as ArrowDownIcon } from 'app/assets/img/arrow_down.svg';
import { ReactComponent as TrashIcon } from 'app/assets/img/trash.svg';

import { formatNumber } from 'utils/helper';
import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import {
  MAP_VGS,
  MAX_VALID_TICKET_AMOUNT,
  TICKET_TYPES,
} from 'utils/constants';
import useWindowSize from 'app/hooks/useWindowSize';
import DialogWarning from 'app/pages/Catalog/components/DialogWarning';
import { I_TicketDataProcessed } from '../../types';
import CustomDatePicker from 'app/pages/Cart/components/TicketItem/partials/components/CustomDatePicker';
import NumberControl from 'app/pages/Cart/components/TicketItem/partials/components/NumberControl';
import GrandstandDiagram from 'app/pages/Cart/components/TicketItem/partials/components/GrandstandDiagram';
import {
  Thumb,
  TicketContainer,
  TicketName,
  Catalog,
  PriceBLock,
  ListedPrice,
  PromotionalPrice,
  ErrMess,
  PositionAndTimeBlock,
  StyledDefaultImg,
  StyledSelect,
} from 'app/pages/Cart/components/TicketItem/partials/components/Styled';
import { OrderContext } from '../..';
import RenderIf from 'app/components/RenderIf';
import Inventory from 'app/pages/Cart/components/TicketItem/partials/components/Inventory';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import checkHaveTicketOnSelectedDate from 'app/pages/Cart/components/TicketItem/partials/helper';
import useGetUsedArea2 from 'app/hooks/useGetUsedArea2';
import { SelectPopupContainer } from 'app/pages/Catalog/FormItemTicket';
import { PriceDisplayStatus } from 'app/pages/Catalog/FormItemTicket';
import _ from 'lodash';
import { PriceWrapper } from '../ShowItem';

type OnGetTicketPrice = (params: {
  show_id?: number;
  date?: string;
  zone_id?: number;
  used_area2?: string | number;
  restaurant_id?: number;
}) => void;
export interface TicketItemProps extends I_TicketDataProcessed {
  jumpTicket?: number;
  minAmountTicket?: number;
  totalTicketInOrder: number;
  onRemoveTicket: (identify: string) => void;
  setLoadingPage: Dispatch<SetStateAction<boolean>>;
  onSetTicket: (
    setter: (prev: I_TicketDataProcessed[]) => I_TicketDataProcessed[],
  ) => void;
  ticketsMergedNeedUpdateAmount: MutableRefObject<string[]>;
  setEnablePaymentAction: Dispatch<SetStateAction<boolean>>;
  actualAmountInOrder?: number;
  isSubmitted: boolean;
  originOrderDetailData: Record<string, any>;
  ticketsGettingPrice: string[];
  setTicketsGettingPrice: Dispatch<SetStateAction<string[]>>;
  bpAllocatedDebt?: number | null;
  setDisableCreateOrder: Dispatch<React.SetStateAction<boolean>>;
  disableCreateOrder: boolean;
  hasChangeProperty: React.MutableRefObject<boolean>;
  isChangingNumberRef: React.MutableRefObject<boolean>;
}

export enum CART_ERROR_TYPE {
  ERROR_TIME = 'errorTime',
  ERROR_TICKET = 'errorTicket',
}

export type ErrorData = {
  keyItem: string;
  message: string;
  errorType: CART_ERROR_TYPE;
};

const TicketItem = (props: TicketItemProps) => {
  const {
    id, // If the ticket already exists in the order, the id will exist, otherwise
    originalPrice,
    amount,
    catalog,
    date,
    show,
    zone,
    identify,
    errorTicket,
    errorTime,
    inventory,
    product,
    restaurant,
    jumpTicket,
    minAmountTicket,
    usedAreas2,
    totalTicketInOrder,
    onRemoveTicket,
    setLoadingPage,
    onSetTicket,
    ticketsMergedNeedUpdateAmount,
    setEnablePaymentAction,
    actualAmountInOrder,
    isSubmitted,
    originOrderDetailData,
    ticketsGettingPrice,
    setTicketsGettingPrice,
    bpAllocatedDebt,
    setDisableCreateOrder,
    disableCreateOrder,
    hasChangeProperty,
    isChangingNumberRef,
  } = props;

  const originTicketData = originOrderDetailData?.items;

  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();

  const [warningDialogMsg, setWarningDialogMsg] = useState('');
  const { onChangeTicketAmount, onUpdateTicket, onChangeAmountError } =
    useContext(OrderContext);
  const [ticketAmount, setTicketAmount] = useState(amount);
  const originAmount = useRef(actualAmountInOrder ?? amount);
  const numberControlRef = useRef<{
    setInternalValue: (value: number) => void;
  }>({ setInternalValue: () => {} });

  const [jumpValue, setJumpValue] = useState(show?.jumpValueB2b ?? 1);
  const [minAmount, setMinAmount] = useState(
    show.minAmountB2b ? +show.minAmountB2b : 0,
  );

  const [triggerValidateRestaurant, setTriggerValidateRestaurant] =
    useState(false);
  const [triggerValidateZone, setTriggerValidateZone] = useState(false);
  const [triggerValidateUsedArea2, setTriggerValidateUsedArea2] =
    useState(false);

  const isHideInventory = ticketsGettingPrice.includes(identify);

  const isLoadedUsedArea2Data = useRef(true);
  const [enableGetUsedArea2, setEnableGetUsedArea2] = useState(false);

  const editOrderRef = useRef<HTMLDivElement>(null);
  const [editOrderDOMRect, setEditOrderDOMRect] = useState<DOMRect | null>(
    null,
  );

  const isComboDifTicket = useMemo(
    () =>
      catalog?.mapVGS === MAP_VGS.COMBO &&
      product?.park?.workstationId === 'DIF',
    [catalog?.mapVGS, product?.park?.workstationId],
  );

  const infoUser = useSelector(selectInfoUser);

  const isShowTag = useMemo(() => {
    if (!!originOrderDetailData?.businessPartnerAllocatedDebt) {
      return (
        !!originOrderDetailData?.businessPartnerAllocatedDebt?.bpLongTerm &&
        !!originOrderDetailData?.businessPartnerAllocatedDebt?.allowBuyShortTerm
      );
    }
    return (
      !!infoUser?.businessPartner?.bpLongTerm &&
      !!infoUser?.businessPartner?.allowBuyShortTerm
    );
  }, [infoUser, originOrderDetailData?.businessPartnerAllocatedDebt]);

  const isHaveTicketOnSelectedDate = useMemo(
    () =>
      checkHaveTicketOnSelectedDate({
        selectedDate: moment(date.dateOrigin, 'YYYY-MM-DD'),
        dateFrom: moment(show?.dateFrom?.dateOrigin, 'YYYY-MM-DD'),
        dateTo: moment(show?.dateTo?.dateOrigin, 'YYYY-MM-DD'),
        isComboDifTicket,
        weekend: show?.weekend,
        fireworkDay: show?.fireworkDay,
      }),
    [
      date.dateOrigin,
      isComboDifTicket,
      show?.dateFrom?.dateOrigin,
      show?.dateTo?.dateOrigin,
      show?.weekend,
      show?.fireworkDay,
    ],
  );

  useEffect(() => {
    if (jumpTicket) setJumpValue(jumpTicket);
  }, [jumpTicket]);

  useEffect(() => {
    if (minAmountTicket) setMinAmount(minAmountTicket);
  }, [minAmountTicket]);

  const translations = useSelector(selectTranslations);

  const prevValidAmout = useRef(+amount);

  const ticketInventory = useMemo(() => {
    const isExistsInOrder = originTicketData?.find(
      i => i.identify === identify,
    );

    if (isExistsInOrder)
      return +(inventory ?? 0) + +(isExistsInOrder?.amount ?? 0);
    return +(inventory ?? 0);
  }, [identify, inventory, originTicketData]);

  const isRestaurantTicket = useMemo(
    () =>
      show?.restaurants &&
      show?.restaurants?.length > 0 &&
      show?.materialType !== TICKET_TYPES.CABLE_CAR,
    [show?.materialType, show?.restaurants],
  );

  const isCableTicket = useMemo(
    () => show?.materialType === TICKET_TYPES.CABLE_CAR,
    [show?.materialType],
  );

  const isNotApplyZone = useMemo(
    () =>
      show?.materialType === TICKET_TYPES.CABLE_CAR ||
      (show.zones?.length ?? 0) === 0,
    [show?.materialType, show.zones?.length],
  );

  const grandstandData = useMemo(() => {
    const restaurantByIdSelected = show?.restaurants?.find(
      i => i?.id === restaurant?.id,
    );
    return restaurantByIdSelected ?? show?.restaurants?.[0];
  }, [restaurant?.id, show?.restaurants]);

  const generateIdentify = useCallback(
    (ticketData: {
      zoneId?: number;
      date?: string;
      restaurantId?: number;
      usedArea2?: string | number;
    }): string => {
      let identify = show.id;
      const partOfIdentity = {
        performanceId: undefined,
        zoneId: zone?.id,
        date: date.dateOrigin,
        restaurantId: restaurant?.id,
        usedArea2: product?.usedArea2Object?.value,
        ...ticketData,
      };

      for (let key in partOfIdentity) {
        const v = partOfIdentity[key];
        identify += '_';
        if (!!v && v !== 'undefined' && v !== 'null') identify += v;
      }

      return identify;
    },
    [
      date.dateOrigin,
      product?.usedArea2Object?.value,
      restaurant?.id,
      show.id,
      zone?.id,
    ],
  );

  const handleGetTicketPrice: OnGetTicketPrice = useCallback(
    params => {
      const ticketData = {
        show_id: show?.id,
        date: date.dateOrigin,
        zone_id: zone?.id,
        restaurant_id: restaurant?.id,
        used_area2: product?.usedArea2Object?.value,
        ...params,
      };

      if (
        (!isCableTicket && !ticketData.used_area2) ||
        (!isNotApplyZone && !ticketData?.zone_id) ||
        (isRestaurantTicket && !ticketData.restaurant_id)
      )
        return false;

      onChangeTicketAmount(identify, amount);
    },
    [
      date.dateOrigin,
      isCableTicket,
      isNotApplyZone,
      isRestaurantTicket,
      product?.usedArea2Object?.value,
      restaurant?.id,
      show?.id,
      zone?.id,
      onChangeTicketAmount,
      amount,
      identify,
    ],
  );

  const handleChangeUsedArea2 = useCallback(
    (_value, option) => {
      hasChangeProperty.current = true;
      setEnablePaymentAction(true);
      let itemRemoved: I_TicketDataProcessed | undefined = undefined;
      let newTicketData: I_TicketDataProcessed | undefined = undefined;
      const newIdentify = generateIdentify({ usedArea2: option.value });

      setTicketsGettingPrice(prev => [...prev, newIdentify]);

      onSetTicket(prev => {
        itemRemoved = prev.find(item => item.identify === newIdentify);

        const isExistsInOrder = originTicketData?.find(
          i => i.identify === newIdentify,
        );

        return prev.reduce((result, item) => {
          switch (true) {
            case newIdentify === item.identify:
              break;
            case identify !== item.identify:
              result.push({ ...item });
              break;
            case identify === item.identify:
            default:
              newTicketData = itemRemoved
                ? {
                    ...itemRemoved,
                    key: item.key,
                    product: {
                      ...itemRemoved.product,
                      usedArea2: option.value,
                      usedArea2Object: { ...option },
                      used_area2: option.value,
                    },
                    identify: newIdentify,
                    amount: +itemRemoved.amount + +amount,
                  }
                : {
                    ...item,
                    id: isExistsInOrder ? isExistsInOrder.id : undefined,
                    product: {
                      ...item.product,
                      usedArea2: option.value,
                      usedArea2Object: { ...option },
                      used_area2: option.value,
                    },
                    identify: newIdentify,
                  };
              result.push(newTicketData);
          }
          return result;
        }, [] as I_TicketDataProcessed[]);
      });

      ticketsMergedNeedUpdateAmount.current = [
        ...ticketsMergedNeedUpdateAmount.current,
        newIdentify,
      ];

      handleGetTicketPrice({
        used_area2: option?.value,
      });
    },
    [
      amount,
      generateIdentify,
      handleGetTicketPrice,
      identify,
      onSetTicket,
      ticketsMergedNeedUpdateAmount,
      setEnablePaymentAction,
      originTicketData,
      setTicketsGettingPrice,
    ],
  );

  const useGetUsedArea2Options = useMemo(
    () => ({
      payloadData: {
        date: date.dateOrigin,
        show_id: show?.id,
        restaurant_id: restaurant?.id,
        zone_id: zone?.id,
        ...(bpAllocatedDebt ? { bpAllocatedDebt: bpAllocatedDebt } : {}),
      },
      applyZone: !isNotApplyZone,
      applyUsedZone2: !isCableTicket,
      isRestaurant: isRestaurantTicket,
      IIFE: enableGetUsedArea2,
      onBefore: () => {
        setLoadingPage(true);
      },
      onSuccess: response => {
        const haveOnlyOneOption = response.data?.length === 1;

        onSetTicket(prev =>
          prev.map(item => {
            if (item.identify !== identify) return item;
            return { ...item, usedAreas2: response.data };
          }),
        );

        if (haveOnlyOneOption) {
          handleChangeUsedArea2(null, {
            label: response.data[0].label,
            value: response.data[0].value,
          });
        } else setLoadingPage(false);
      },
      onError: () => {
        setLoadingPage(false);
        onSetTicket(prev =>
          prev.map(item => {
            if (item.identify !== identify) return item;
            return { ...item, usedAreas2: [] };
          }),
        );
      },
      onFinally: () => {
        setEnableGetUsedArea2(false);
        isLoadedUsedArea2Data.current = true;
      },
    }),
    [
      date.dateOrigin,
      enableGetUsedArea2,
      identify,
      isCableTicket,
      isNotApplyZone,
      isRestaurantTicket,
      restaurant?.id,
      show?.id,
      zone?.id,
      onSetTicket,
      setLoadingPage,
      handleChangeUsedArea2,
      bpAllocatedDebt,
    ],
  );

  const displayOriginalPriceFormat = () => {
    const price = originalPrice;
    const originalPriceDisplay = product?.originalPrice;

    let displayStatus: string = PriceDisplayStatus.NA;

    const handleChangeStaus = (
      disableCreateOrderValue: boolean,
      displayStatusValue: string,
    ) => {
      if (hasChangeProperty) {
        setDisableCreateOrder(disableCreateOrderValue);
      }

      displayStatus = displayStatusValue;
    };

    (() => {
      if (!hasChangeProperty.current && originalPriceDisplay > price) {
        handleChangeStaus(false, PriceDisplayStatus.SHOW_BASE_PRICE);
        return;
      }
      if (!hasChangeProperty.current && originalPriceDisplay <= price) {
        handleChangeStaus(false, PriceDisplayStatus.SHOW_PRICE_ONLY);
        return;
      }

      if (_.isNil(originalPriceDisplay)) {
        // Không có ngày chọn hoặc không có giá cơ bản sau khi fetch → NA
        handleChangeStaus(true, PriceDisplayStatus.NA);
        return;
      }

      if (_.isNil(price)) {
        // Không có giá bán
        handleChangeStaus(true, PriceDisplayStatus.NA);
        return;
      }

      if (price === 0) {
        // Có CB và B = 0 → Hiển thị CB + B (bình thường) nhưng không mua được
        handleChangeStaus(true, PriceDisplayStatus.SHOW_BASE_PRICE);
        return;
      }

      if (originalPriceDisplay > price) {
        // CB > B → Hiển thị CB + B → mua được
        handleChangeStaus(false, PriceDisplayStatus.SHOW_BASE_PRICE);
        return;
      }

      if (originalPriceDisplay === 0 || originalPriceDisplay <= price) {
        // CB == B hoặc CB < B hoặc CB = 0 → chỉ hiển thị B → mua được
        handleChangeStaus(false, PriceDisplayStatus.SHOW_PRICE_ONLY);
        return;
      }

      // Trường hợp còn lại → NA
      handleChangeStaus(true, PriceDisplayStatus.NA);
    })();

    switch (displayStatus) {
      case PriceDisplayStatus.NA:
        return (
          <div className="--price" style={{ position: 'relative', top: 6 }}>
            <span className="--price-na">N/Ađ</span>
          </div>
        );

      case PriceDisplayStatus.SHOW_BASE_PRICE:
        return (
          <>
            <del className="--price-old">
              {formatNumber(originalPriceDisplay || 0)}đ
            </del>
            <div className="--price" style={{ position: 'static', top: 6 }}>
              {formatNumber(price || 0)}đ
            </div>
          </>
        );

      case PriceDisplayStatus.SHOW_PRICE_ONLY:
        return (
          <div className="--price" style={{ position: 'static', top: 6 }}>
            {formatNumber(price || 0)}đ
          </div>
        );

      default:
        return (
          <div className="--price" style={{ position: 'relative', top: 6 }}>
            <span className="--price-na">N/Ađ</span>
          </div>
        );
    }
  };

  useGetUsedArea2(useGetUsedArea2Options);

  const convertDataOption = (arr, type) => {
    let result = [];
    if (arr?.length) {
      result = arr.map(item => ({
        ...item,
        value: type === 'usedArea2' ? item.value : item.id,
        label: type === 'usedArea2' ? item.label : item?.name,
      }));
    }
    return result;
  };

  const handleDecrementNumber = () => {
    hasChangeProperty.current = true;
    let newAmount = ticketAmount - jumpValue;
    newAmount = newAmount < 0 ? 0 : newAmount;
    if (newAmount < minAmount) {
      onUpdateTicket(identify, {
        errorTicket:
          translations?.['b2c.catalog-detail.min-amount'].replace(
            '${number}',
            minAmount,
          ) ?? '',
      });
      onChangeAmountError(identify, newAmount);
      return;
    }
    setEnablePaymentAction(true);
    onUpdateTicket(identify, { errorTicket: null });
    onChangeTicketAmount(identify, newAmount);
    prevValidAmout.current = newAmount;
  };

  const handleIncrementNumber = () => {
    hasChangeProperty.current = true;
    const newAmount =
      ticketAmount < minAmount ? minAmount : ticketAmount + jumpValue;

    if (newAmount < minAmount) {
      onUpdateTicket(identify, {
        errorTicket:
          translations?.['b2c.catalog-detail.min-amount'].replace(
            '${number}',
            minAmount,
          ) ?? '',
      });
      onChangeAmountError(identify, newAmount);
      return;
    }
    setEnablePaymentAction(true);
    onUpdateTicket(identify, { errorTicket: null });
    onChangeTicketAmount(identify, newAmount);
    prevValidAmout.current = newAmount;
  };

  const handleChangeTime = (_, dateString: string) => {
    hasChangeProperty.current = true;
    setDisableCreateOrder(false);
    setEnablePaymentAction(true);
    const onlyOneRestaurentOption = show?.restaurants?.length === 1;
    const restaurantId = onlyOneRestaurentOption
      ? show?.restaurants?.[0]?.id
      : undefined;

    const onlyOneZoneOption = show?.zones?.length === 1;
    const zoneId = onlyOneZoneOption ? show?.zones?.[0]?.id : undefined;

    const newIdentify = generateIdentify({
      date: dateString,
      usedArea2: undefined,
      zoneId,
      restaurantId,
    });

    let itemRemoved: I_TicketDataProcessed | undefined = undefined;
    let newTicketData: I_TicketDataProcessed | null = null;

    setEnableGetUsedArea2(true);
    setTicketsGettingPrice(prev => [...prev, newIdentify]);

    onSetTicket(prev => {
      itemRemoved = prev.find(item => item.identify === newIdentify);
      const isExistsInOrder = originTicketData?.find(
        i => i.identify === newIdentify,
      );

      return prev.reduce((result, item) => {
        switch (true) {
          case newIdentify === item.identify:
            break;
          case identify !== item.identify:
            result.push(item);
            break;
          case identify === item.identify:
          default:
            newTicketData = itemRemoved
              ? {
                  ...itemRemoved,
                  key: item.key,
                  date: { dateOrigin: dateString },
                  identify: newIdentify,
                  amount: +itemRemoved.amount + +amount,
                  errorTicket: '',
                  errorTime: '',
                }
              : {
                  ...item,
                  id: isExistsInOrder ? isExistsInOrder.id : undefined,
                  identify: newIdentify,
                  date: { dateOrigin: dateString },
                  errorTicket: '',
                  errorTime: '',
                  zone: isNotApplyZone
                    ? null
                    : onlyOneZoneOption
                    ? show?.zones?.[0]
                    : {},
                  restaurant: onlyOneRestaurentOption
                    ? show?.restaurants?.[0]
                    : null,
                  product: {
                    ...item.product,
                    usedArea2: null,
                    usedArea2Object: null,
                    used_area2: null,
                  },
                  usedAreas2: [],
                };
            result.push(newTicketData);
        }
        return result;
      }, [] as I_TicketDataProcessed[]);
    });

    if (!itemRemoved) isLoadedUsedArea2Data.current = false;

    ticketsMergedNeedUpdateAmount.current = [
      ...ticketsMergedNeedUpdateAmount.current,
      newIdentify,
    ];

    handleGetTicketPrice({
      date: dateString,
      zone_id: undefined,
      used_area2: undefined,
      restaurant_id: undefined,
    });
  };

  const handleChangeRestaurant = (_value, option) => {
    hasChangeProperty.current = true;
    setEnablePaymentAction(true);
    const newIdentify = generateIdentify({
      restaurantId: option?.id,
      usedArea2: undefined,
    });
    let itemRemoved: I_TicketDataProcessed | undefined = undefined;
    let newTicketData: I_TicketDataProcessed | null = null;

    setEnableGetUsedArea2(true);
    setTicketsGettingPrice(prev => [...prev, newIdentify]);

    onSetTicket(prev => {
      itemRemoved = prev.find(i => i.identify === newIdentify);

      const isExistsInOrder = originTicketData?.find(
        i => i.identify === newIdentify,
      );

      return prev.reduce((result, item) => {
        switch (true) {
          case newIdentify === item.identify:
            break;
          case identify !== item.identify:
            result.push(item);
            break;
          case identify === item.identify:
          default:
            newTicketData = itemRemoved
              ? {
                  ...itemRemoved,
                  key: item.key,
                  restaurant: {
                    ...option,
                    name: option.label,
                  },
                  identify: newIdentify,
                  amount: +itemRemoved.amount + +amount,
                  errorTicket: '',
                  errorTime: '',
                }
              : {
                  ...item,
                  id: isExistsInOrder ? isExistsInOrder.id : undefined,
                  restaurant: {
                    ...option,
                    name: option.label,
                  },
                  identify: newIdentify,
                  errorTicket: '',
                  errorTime: '',
                  zone: {},
                  product: {
                    ...item.product,
                    usedArea2: null,
                    usedArea2Object: null,
                    used_area2: null,
                  },
                  usedAreas2: [],
                };
            result.push(newTicketData);
        }
        return result;
      }, [] as I_TicketDataProcessed[]);
    });

    if (!itemRemoved) isLoadedUsedArea2Data.current = false;

    ticketsMergedNeedUpdateAmount.current = [
      ...ticketsMergedNeedUpdateAmount.current,
      newIdentify,
    ];
  };

  const handleChangeZone = (_value, option) => {
    hasChangeProperty.current = true;
    setEnablePaymentAction(true);
    const newIdentify = generateIdentify({
      zoneId: option?.id,
      usedArea2: undefined,
    });
    let itemRemoved: I_TicketDataProcessed | undefined = undefined;
    let newTicketData: I_TicketDataProcessed | null = null;

    setEnableGetUsedArea2(true);
    setTicketsGettingPrice(prev => [...prev, newIdentify]);

    onSetTicket(prev => {
      itemRemoved = prev.find(i => i.identify === newIdentify);

      const isExistsInOrder = originTicketData?.find(
        i => i.identify === newIdentify,
      );

      return prev.reduce((result, item) => {
        switch (true) {
          case newIdentify === item.identify:
            break;
          case identify !== item.identify:
            result.push(item);
            break;
          case identify === item.identify:
          default:
            newTicketData = itemRemoved
              ? {
                  ...itemRemoved,
                  key: item.key,
                  zone: option,
                  identify: newIdentify,
                  amount: +itemRemoved.amount + +amount,
                  errorTicket: '',
                  errorTime: '',
                }
              : {
                  ...item,
                  id: isExistsInOrder ? isExistsInOrder.id : undefined,
                  zone: option,
                  identify: newIdentify,
                  errorTicket: '',
                  errorTime: '',
                  product: {
                    ...item.product,
                    usedArea2: null,
                    usedArea2Object: null,
                    used_area2: null,
                  },
                  usedAreas2: [],
                };
            result.push(newTicketData);
        }
        return result;
      }, [] as I_TicketDataProcessed[]);
    });

    if (!itemRemoved) isLoadedUsedArea2Data.current = false;

    ticketsMergedNeedUpdateAmount.current = [
      ...ticketsMergedNeedUpdateAmount.current,
      newIdentify,
    ];
  };

  const handleOnChangeNumber = (value: number) => {
    hasChangeProperty.current = true;
    isChangingNumberRef.current = true;
    setEnablePaymentAction(true);
    const maxValueByJumpValue =
      Math.floor(
        Math.min(ticketInventory, MAX_VALID_TICKET_AMOUNT) / jumpValue,
      ) * jumpValue;
    let newValue = value < 1 ? 1 : value;
    if (newValue > maxValueByJumpValue) newValue = maxValueByJumpValue;

    if (newValue < minAmount) {
      onUpdateTicket(identify, {
        errorTicket:
          translations?.['b2c.catalog-detail.min-amount'].replace(
            '${number}',
            minAmount,
          ) ?? '',
      });
      onChangeAmountError(
        identify,
        Math.min(newValue, ticketInventory, MAX_VALID_TICKET_AMOUNT),
      );
      return;
    }

    if (newValue % jumpValue !== 0) {
      onUpdateTicket(identify, { errorTicket: null });
      onChangeAmountError(identify, newValue);
      return;
    }

    onUpdateTicket(identify, { errorTicket: null });
    onChangeAmountError(identify, newValue);
    if (
      newValue > MAX_VALID_TICKET_AMOUNT &&
      ticketInventory > MAX_VALID_TICKET_AMOUNT
    ) {
      onUpdateTicket(identify, {
        errorTicket: translations?.['b2b.txt.max10000tickets'] ?? '',
      });
    }
  };

  const handleOnBlur = () => {
    if (amount % jumpValue !== 0 && !errorTicket) {
      setWarningDialogMsg(
        translations?.['b2b.txt.onlyAmount']?.replace(
          '${jumpValue}',
          jumpValue,
        ),
      );
      if (prevValidAmout.current < minAmount) {
        onUpdateTicket(identify, {
          errorTicket:
            translations?.['b2c.catalog-detail.min-amount'].replace(
              '${number}',
              minAmount,
            ) ?? '',
        });
      }
      onChangeAmountError(identify, prevValidAmout.current);
      numberControlRef.current.setInternalValue(prevValidAmout.current);
    } else if (amount >= minAmount && amount % (jumpTicket ?? 1) === 0) {
      const newAmount = Math.min(
        amount,
        ticketInventory,
        MAX_VALID_TICKET_AMOUNT,
      );
      onChangeTicketAmount(identify, newAmount);
      prevValidAmout.current = newAmount;
      numberControlRef.current.setInternalValue(newAmount);
    }
  };

  const isCanRemove = totalTicketInOrder > 1;

  const handleRemove = () => {
    if (!isCanRemove) return;

    onOpenDialogNoti({
      type: 'ERROR',
      isShow: true,
      title: '',
      content: translations?.['b2b.txt.cfRemoveTicketInOrder'] ?? '',
      closable: false,
      buttons: [
        {
          text: translations?.['b2b.btn.cancel'] ?? '',
          bg: '#ffff',
          border: 'solid 1px #808080',
          color: '#808080',
          onClick: () => {
            onCloseDialogNoti();
          },
        },
        {
          text: translations?.['b2b.btn.delete'] ?? '',
          bg: 'linear-gradient(79.97deg, #F06F24 -7.79%, #F8B117 107.61%)',
          color: 'white',
          onClick: () => {
            setDisableCreateOrder(false);
            onRemoveTicket(identify);
            onCloseDialogNoti();
          },
        },
      ],
    });
  };

  const { device } = useWindowSize();
  const isDesktopScreen = useMemo(() => device === 'desktop', [device]);

  useEffect(() => {
    setTicketAmount(amount);
  }, [amount]);

  useLayoutEffect(() => {
    const idx = ticketsMergedNeedUpdateAmount.current.indexOf(identify!);
    if (idx > -1) {
      numberControlRef.current.setInternalValue(+amount!);
      ticketsMergedNeedUpdateAmount.current.splice(idx, 1);
    }
  });

  useEffect(() => {
    if (isSubmitted) {
      setTriggerValidateUsedArea2(true);
      setTriggerValidateZone(true);
      setTriggerValidateRestaurant(true);
    }
  }, [isSubmitted]);

  const selectRestaurantPopupContainer = useRef<HTMLDivElement>(null);
  const selectZonePopupContainer = useRef<HTMLDivElement>(null);
  const selectPerformancePopupContainer = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useLayoutEffect(() => {
    const onSetGroupTicketDetailDOMRect = () => {
      if (!editOrderRef.current) return;
      setEditOrderDOMRect(editOrderRef.current.getBoundingClientRect());
    };

    onSetGroupTicketDetailDOMRect();

    window.addEventListener('resize', onSetGroupTicketDetailDOMRect);

    return () => {
      window.removeEventListener('resize', onSetGroupTicketDetailDOMRect);
    };
  }, []);

  return (
    <>
      <TicketContainer
        gutter={33}
        style={{ flexDirection: isDesktopScreen ? 'row' : 'column' }}
        id={identify!}
      >
        <Col span={isDesktopScreen ? 5 : 24}>
          {show?.image?.original ? (
            <Thumb src={show?.image?.original} />
          ) : (
            <StyledDefaultImg />
          )}
        </Col>
        <Col span={isDesktopScreen ? 19 : 24}>
          {isDesktopScreen ? (
            <Row
              justify="space-between"
              style={{
                flexWrap: 'nowrap',
                flexDirection: 'row',
              }}
            >
              <Col
                style={{
                  maxWidth: 'calc(100% - 216px)',
                }}
              >
                <TicketName>{catalog?.name}</TicketName>
              </Col>
              <Col>
                <TrashIcon
                  style={{
                    cursor: isCanRemove ? 'pointer' : 'auto',
                    marginRight: 16,
                    filter: isCanRemove
                      ? 'none'
                      : 'contrast(0.1) brightness(1.6)',
                  }}
                  onClick={handleRemove}
                />
              </Col>
            </Row>
          ) : (
            <Row
              justify="space-between"
              style={{
                flexWrap: 'nowrap',
                flexDirection: 'column',
              }}
            >
              <Col
                span={24}
                style={{
                  maxWidth: 'auto',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <TicketName>{catalog?.name}</TicketName>
                <TrashIcon
                  style={{
                    cursor: isCanRemove ? 'pointer' : 'auto',
                    filter: isCanRemove
                      ? 'none'
                      : 'contrast(0.1) brightness(1.6)',
                  }}
                  onClick={handleRemove}
                />
              </Col>
            </Row>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Catalog>{show?.name}</Catalog>
            {isShowTag && (
              <div
                style={{
                  marginBottom: '5px',
                  padding: '4px 8px',
                  whiteSpace: 'nowrap',
                  background: '#F54949',
                  borderRadius: '8px',
                  color: 'white',
                  height: '25px',
                  fontSize: '12px',
                }}
              >
                {
                  translations?.[
                    `${
                      product?.isLongTerm
                        ? 'b2b.txt.long-term'
                        : 'b2b.txt.short-term'
                    }`
                  ]
                }
              </div>
            )}
          </div>
          <div>
            <Row gutter={32}>
              <Col span={isDesktopScreen ? 9 : 24}>
                <div
                  style={{
                    position: 'relative',
                    top: isDesktopScreen ? 2 : 0,
                    marginTop: isDesktopScreen ? 0 : 8,
                  }}
                >
                  <CustomDatePicker
                    value={moment(date?.dateOrigin, 'YYYY-MM-DD')}
                    format="YYYY-MM-DD"
                    isComboDiffTicket={false}
                    showDateFrom={show?.dateFrom}
                    showDateTo={show?.dateTo}
                    weekend={show?.weekend}
                    onChange={handleChangeTime}
                    isError={
                      (!isCableTicket &&
                        isLoadedUsedArea2Data.current &&
                        (usedAreas2 ?? []).length < 1) ||
                      !!errorTime ||
                      !isHaveTicketOnSelectedDate
                    }
                    fireworkDay={show?.fireworkDay}
                  />
                  <RenderIf
                    condition={
                      (!isCableTicket &&
                        isLoadedUsedArea2Data.current &&
                        (usedAreas2 ?? []).length < 1) ||
                      !!errorTime ||
                      !isHaveTicketOnSelectedDate
                    }
                  >
                    <ErrMess>
                      {errorTime ||
                        translations?.['b2c.txt.detail-show-no-performance']}
                    </ErrMess>
                  </RenderIf>
                </div>
              </Col>
              {isDesktopScreen && (
                <PriceBLock>
                  <PriceWrapper>{displayOriginalPriceFormat()}</PriceWrapper>
                </PriceBLock>
              )}
              {isDesktopScreen && (
                <Col>
                  <NumberControl
                    number={ticketAmount}
                    onDecrement={handleDecrementNumber}
                    onIncrement={handleIncrementNumber}
                    onChange={handleOnChangeNumber}
                    disabledMinus={
                      Number(ticketAmount) <= (minAmount || 1) ||
                      isHideInventory
                    }
                    disablePlus={
                      ticketAmount >=
                        Math.floor(
                          Math.min(+ticketInventory, MAX_VALID_TICKET_AMOUNT) /
                            jumpValue,
                        ) *
                          jumpValue || isHideInventory
                    }
                    min={1}
                    max={
                      Math.floor(
                        Math.min(+ticketInventory, MAX_VALID_TICKET_AMOUNT) /
                          jumpValue,
                      ) * jumpValue
                    }
                    step={jumpValue}
                    onBlur={handleOnBlur}
                    ref={numberControlRef}
                    lowerLimit={minAmount}
                    inputReadOnly={isHideInventory}
                  />
                  <RenderIf condition={!!errorTicket}>
                    <ErrMess style={{ maxWidth: 180 }}>{errorTicket}</ErrMess>
                  </RenderIf>
                </Col>
              )}
            </Row>
          </div>
          {!isCableTicket || isDesktopScreen ? (
            <div ref={editOrderRef}>
              <PositionAndTimeBlock gutter={isDesktopScreen ? 24 : 16}>
                <RenderIf
                  condition={isRestaurantTicket && !isCableTicket}
                  fallback={<Col span={isDesktopScreen ? 6 : 12} />}
                >
                  <Col span={isDesktopScreen ? 6 : 12}>
                    <SelectPopupContainer
                      ref={selectRestaurantPopupContainer}
                      popupMaxWidth={
                        !!editOrderDOMRect?.width
                          ? `${editOrderDOMRect?.width}px`
                          : undefined
                      }
                      popupWidth={
                        device === 'desktop'
                          ? 'initial'
                          : !!editOrderDOMRect?.width
                          ? `${editOrderDOMRect?.width}px`
                          : undefined
                      }
                    />
                    <StyledSelect
                      options={
                        convertDataOption(
                          show?.restaurants ?? [],
                          'restaurant',
                        ) ?? []
                      }
                      dropdownRender={menu => (
                        <div className="custom-dropdown">{menu}</div>
                      )}
                      virtual={false}
                      value={restaurant?.id}
                      onChange={handleChangeRestaurant}
                      placeholder={
                        show.usedArea1Label ||
                        translations?.['b2c.text.restaurants'] ||
                        ''
                      }
                      getPopupContainer={() =>
                        selectRestaurantPopupContainer.current!
                      }
                      onBlur={() => setTriggerValidateRestaurant(true)}
                      suffixIcon={<ArrowDownIcon />}
                      isError={triggerValidateRestaurant && !restaurant?.id}
                    />
                    <RenderIf
                      condition={triggerValidateRestaurant && !restaurant?.id}
                    >
                      <ErrMess>
                        {`${translations?.['b2c.txt.plsChoose']} ${
                          show?.usedArea1Label?.toLowerCase() ?? ''
                        }`}
                      </ErrMess>
                    </RenderIf>
                  </Col>
                </RenderIf>

                <RenderIf condition={!isCableTicket}>
                  <>
                    <RenderIf condition={!isNotApplyZone}>
                      <Col span={isDesktopScreen ? 6 : 12}>
                        <SelectPopupContainer
                          ref={selectZonePopupContainer}
                          popupMaxWidth={
                            !!editOrderDOMRect?.width
                              ? `${editOrderDOMRect?.width}px`
                              : undefined
                          }
                          popupWidth={
                            device === 'desktop'
                              ? 'initial'
                              : !!editOrderDOMRect?.width
                              ? `${editOrderDOMRect?.width}px`
                              : undefined
                          }
                        />
                        <StyledSelect
                          options={
                            convertDataOption(show?.zones ?? [], 'zone') ?? []
                          }
                          dropdownRender={menu => (
                            <div className="custom-dropdown">{menu}</div>
                          )}
                          virtual={false}
                          value={zone?.id}
                          onChange={handleChangeZone}
                          placeholder={translations?.['b2c.txt.zone']}
                          onBlur={() => setTriggerValidateZone(true)}
                          suffixIcon={<ArrowDownIcon />}
                          isError={triggerValidateZone && !zone?.id}
                          getPopupContainer={() =>
                            selectZonePopupContainer.current!
                          }
                          placement={
                            device !== 'desktop' ? 'bottomRight' : 'bottomLeft'
                          }
                        />
                        <RenderIf condition={triggerValidateZone && !zone?.id}>
                          <ErrMess>
                            {translations?.['b2b.txt.validateZone']}
                          </ErrMess>
                        </RenderIf>
                      </Col>
                    </RenderIf>
                    <Col span={isDesktopScreen ? 6 : 12}>
                      <RenderIf condition={!isCableTicket}>
                        <>
                          <SelectPopupContainer
                            ref={selectPerformancePopupContainer}
                            popupMaxWidth={
                              !!editOrderDOMRect?.width
                                ? `${editOrderDOMRect?.width}px`
                                : undefined
                            }
                            popupWidth={
                              device === 'desktop'
                                ? 'initial'
                                : !!editOrderDOMRect?.width
                                ? `${editOrderDOMRect?.width}px`
                                : undefined
                            }
                          />
                          <StyledSelect
                            options={usedAreas2 ?? []}
                            dropdownRender={menu => (
                              <div className="custom-dropdown">{menu}</div>
                            )}
                            virtual={false}
                            value={product?.usedArea2Object?.value}
                            onChange={handleChangeUsedArea2}
                            placeholder={
                              show?.usedArea2Label ||
                              translations?.['b2c.txt.hours'] ||
                              ''
                            }
                            onBlur={() => setTriggerValidateUsedArea2(true)}
                            suffixIcon={<ArrowDownIcon />}
                            isError={
                              triggerValidateUsedArea2 &&
                              !product?.usedArea2Object?.value
                            }
                            getPopupContainer={() =>
                              selectPerformancePopupContainer.current!
                            }
                            placement={
                              device !== 'desktop' && !!isNotApplyZone
                                ? 'bottomRight'
                                : 'bottomLeft'
                            }
                          />
                          <RenderIf
                            condition={
                              triggerValidateUsedArea2 &&
                              !product?.usedArea2Object?.value
                            }
                          >
                            <ErrMess>
                              {`${translations?.['b2c.txt.plsChoose']} ${
                                show?.usedArea2Label?.toLowerCase() ?? ''
                              }`}
                            </ErrMess>
                          </RenderIf>
                        </>
                      </RenderIf>
                    </Col>
                  </>
                </RenderIf>

                {isDesktopScreen ? (
                  <Col
                    offset={
                      (2 -
                        [
                          !isCableTicket,
                          !isNotApplyZone,
                          isRestaurantTicket,
                        ].filter(i => i).length) *
                      6
                    }
                  >
                    <div
                      style={{
                        marginLeft: 65,
                        paddingTop: 4,
                      }}
                    >
                      {!isHideInventory && (
                        <Inventory
                          inventory={ticketInventory}
                          styles={{ maxWidth: 200 }}
                        />
                      )}
                    </div>
                  </Col>
                ) : null}
              </PositionAndTimeBlock>
            </div>
          ) : null}
          {!isDesktopScreen && (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: 16,
                }}
              >
                <GrandstandDiagram
                  grandstandData={grandstandData}
                  textCSS={{ marginTop: 0 }}
                />
                <RenderIf condition={ticketInventory !== null}>
                  <>
                    {!isHideInventory && (
                      <Inventory inventory={ticketInventory!} />
                    )}
                  </>
                </RenderIf>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: 16,
                }}
              >
                <PriceBLock
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                  }}
                >
                  <RenderIf condition={product?.originalPrice > originalPrice}>
                    <ListedPrice>
                      {formatNumber(product?.originalPrice ?? 0).replaceAll(
                        ',',
                        '.',
                      ) + 'đ'}
                    </ListedPrice>
                  </RenderIf>
                  <PromotionalPrice
                    style={{
                      transform: `translateY(${
                        product?.originalPrice > originalPrice ? '0px' : '18px'
                      })`,
                    }}
                  >
                    {formatNumber(originalPrice).replaceAll(',', '.') + 'đ'}
                  </PromotionalPrice>
                </PriceBLock>
                <div className="d-flex flex-column align-items-end">
                  <NumberControl
                    number={ticketAmount}
                    onDecrement={handleDecrementNumber}
                    onIncrement={handleIncrementNumber}
                    disabledMinus={
                      Number(ticketAmount) <= (minAmount || 1) ||
                      isHideInventory
                    }
                    onChange={handleOnChangeNumber}
                    disablePlus={
                      +ticketAmount >= ticketInventory ||
                      ticketAmount >= MAX_VALID_TICKET_AMOUNT ||
                      isHideInventory
                    }
                    min={1}
                    max={Math.min(ticketInventory, MAX_VALID_TICKET_AMOUNT)}
                    step={jumpValue}
                    ref={numberControlRef}
                    lowerLimit={minAmount}
                    inputReadOnly={isHideInventory}
                  />
                  <RenderIf condition={!!errorTicket}>
                    <ErrMess
                      style={{
                        marginTop: 10,
                        width: 'initial',
                        textAlign: 'right',
                      }}
                    >
                      {errorTicket}
                    </ErrMess>
                  </RenderIf>
                </div>
              </div>
            </>
          )}
          <RenderIf condition={isDesktopScreen}>
            <Row justify="space-between">
              <Col>
                <GrandstandDiagram grandstandData={grandstandData} />
              </Col>
            </Row>
          </RenderIf>
        </Col>
      </TicketContainer>
      <DialogWarning
        open={!!warningDialogMsg}
        message={warningDialogMsg}
        onClose={() => {
          setWarningDialogMsg('');
        }}
      />
    </>
  );
};

export default TicketItem;

const UsageDate = styled.p`
  margin: 0;
  color: #000000;
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  margin-top: 6px;
`;
